import { FilterOption, Options, Table } from '@ckprivate/ckf-ui';
import {
  ActionIcon,
  Button,
  getDefaultZIndex,
  Group,
  Modal,
  Text,
  Title,
} from '@mantine/core';
import type {
  DatesRangeValue,
  DateValue,
} from '@mantine/dates/lib/types/DatePickerValue';
import { modals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconCheck,
  IconFileTypePdf,
  IconUserUp,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { DataTableColumn } from 'mantine-datatable';
import React, { useMemo, useState } from 'react';

import CustomTooltip from '../../../../../components/CustomTooltip';
import useFilePreviewModal from '../../../../../components/FileViewer/useFilePreviewModal';
import { useAuth } from '../../../../../contexts/AuthProvider';
import { useI18n } from '../../../../../contexts/I18nProvider';
import PayslipService from '../../../../../services/PayslipService';
import type { PayslipMonthResponse } from '../../../../../types/types';
import { handleOpenPayslipPdfFile } from '../../../../../utils/attachmentFile';
import handleErrorMessage from '../../../../../utils/handleErrorMessage';
import namesShareAWord from '../../../../../utils/namesShareAWord';
import {
  getFilter,
  getHiddenColumns,
  saveFilter,
  saveHiddenColumns,
} from '../../../../../utils/optionsPreferences';

const componentName = 'AssignEmployeeWithoutPayslipTable';

type Props = {
  payslipsMonthInfo: PayslipMonthResponse | undefined;
  selectDate: DateValue | DatesRangeValue | Date[];
  selectedUserId: string;
  selectedUserFullname: string;
  companyId: string;
};

// Au-dessus de la modale « Assigner » sans passer par la pile `modals` de Mantine, qui démonterait
// cet écran et sa mutation en cours (cf. useFilePreviewModal).
const CONFIRM_Z_INDEX = getDefaultZIndex('modal') + 2;

export default function AssignEmployeeWithoutPayslipTable({
  payslipsMonthInfo,
  selectDate,
  selectedUserId,
  selectedUserFullname,
  companyId,
}: Props) {
  const { access_token } = useAuth();
  const { t, lang } = useI18n();
  const queryClient = useQueryClient();
  const { openFilePreview, filePreviewModal } = useFilePreviewModal();
  // Bulletin dont le nom lu ne ressemble pas au collaborateur choisi, en attente de confirmation.
  const [mismatchedPayslip, setMismatchedPayslip] = useState<{
    userId: string;
    fullname: string;
  } | null>(null);

  const { mutate: reassignPayslip, isLoading: isReassignPayslipLoading } =
    useMutation({
      // Le bulletin sans collaborateur (id provisoire) est l'ancien porteur, le collaborateur
      // choisi le nouveau : inversés, le back cherchait un utilisateur inexistant (« Non autorisé »).
      mutationFn: (variables: {
        payslipUserId: string;
        year: number;
        month: number;
      }) =>
        PayslipService.reassignPayslip(
          variables.payslipUserId,
          selectedUserId,
          variables.year,
          variables.month
        ),
      onSuccess: () => {
        showNotification({
          id: 'reassign-payslip-successful',
          title: t('w.success'),
          message: t('w.assignedPayslip'),
          color: 'green',
          icon: <IconCheck />,
        });
        queryClient.invalidateQueries({
          queryKey: ['PayslipService.getPayslipsMonthInfo', companyId],
        });
        modals.close(`assign-payslip-to-user-${selectedUserId}`);
      },
      onError: (error) =>
        showNotification({
          id: 'reassign-payslip-error',
          title: t('w.error'),
          message: handleErrorMessage(error, t),
          color: 'red',
          icon: <IconX />,
        }),
    });

  const [options, setOptions] = useState<Options>({
    sort: { columnAccessor: '', direction: 'asc' },
    search: {
      accessor: 'fullname',
      label: t('w.name'),
    },
    hiddenColumns: getHiddenColumns({
      componentName,
      hiddenColumns: [''],
    }),
    updateHiddenColumns,
    filter: getFilter({
      componentName,
      filter: [],
    }),
    updateFilter,
  });

  const columns: DataTableColumn<any>[] = useMemo(
    () => [
      {
        accessor: 'fullname',
        title: t('w.name'),
        sortable: true,
        ellipsis: true,
        render: ({ fullname }) => <Text fz={'sm'}>{fullname}</Text>,
      },
      {
        accessor: 'userId',
        title: t('w.payslip'),
        sortable: true,
        ellipsis: true,
        render: ({ userId, fullname }) => (
          <CustomTooltip label={t('w.see')}>
            <ActionIcon
              variant={'subtle'}
              onClick={() =>
                handleOpenPayslipPdfFile(
                  userId,
                  fullname,
                  selectDate,
                  payslipsMonthInfo,
                  access_token,
                  t,
                  openFilePreview
                )
              }
            >
              <IconFileTypePdf />
            </ActionIcon>
          </CustomTooltip>
        ),
      },
      {
        accessor: 'actions',
        title: 'Actions',
        width: '100px',
        sortable: true,
        ellipsis: true,
        render: ({ userId, fullname }) => (
          <Group gap={4} justify="center" wrap={'nowrap'}>
            <CustomTooltip label={t('w.assign')}>
              <ActionIcon
                size="sm"
                color="blue"
                variant={'subtle'}
                onClick={() =>
                  namesShareAWord(fullname, selectedUserFullname)
                    ? assignPayslip(userId)
                    : setMismatchedPayslip({ userId, fullname })
                }
              >
                <IconUserUp size={16} />
              </ActionIcon>
            </CustomTooltip>
          </Group>
        ),
      },
    ],
    []
  );

  function assignPayslip(payslipUserId: string) {
    reassignPayslip({
      payslipUserId,
      // @ts-ignore
      year: dayjs(selectDate).year(),
      // @ts-ignore
      month: dayjs(selectDate).month() + 1,
    });
  }

  function updateFilter(newFilter: FilterOption[]) {
    const _options = { ...options };
    _options.filter = newFilter;
    saveFilter({ componentName, filter: newFilter });
    setOptions(_options);
  }

  function updateHiddenColumns(newHiddenColumns: string[]) {
    const _options = { ...options };
    _options.hiddenColumns = newHiddenColumns;
    saveHiddenColumns({ componentName, hiddenColumns: newHiddenColumns });
    setOptions(_options);
  }

  return (
    <>
      {filePreviewModal}
      <Modal
        opened={mismatchedPayslip !== null}
        onClose={() => setMismatchedPayslip(null)}
        zIndex={CONFIRM_Z_INDEX}
        title={
          <Title size={'h3'} component="p">
            {t('payslips.assign.nameMismatchTitle')}
          </Title>
        }
      >
        <Text>
          {t(
            'payslips.assign.nameMismatchMessage',
            mismatchedPayslip?.fullname,
            selectedUserFullname
          )}
        </Text>
        <Group justify={'flex-end'} mt={'md'}>
          <Button
            variant={'default'}
            onClick={() => setMismatchedPayslip(null)}
          >
            {t('w.cancel')}
          </Button>
          <Button
            color={'red'}
            onClick={() => {
              if (mismatchedPayslip) assignPayslip(mismatchedPayslip.userId);
              setMismatchedPayslip(null);
            }}
          >
            {t('w.assign')}
          </Button>
        </Group>
      </Modal>
      <Table
        options={options}
        columns={columns}
        rows={payslipsMonthInfo?.new}
        lang={lang}
        idAccessor={'userId'}
        fetching={isReassignPayslipLoading}
      />
    </>
  );
}
